import React, { useState, useEffect, useRef } from "react";
import { Globe, Check, ChevronDown } from "lucide-react";

const LANGS = [
  { code: "en", label: "English" },
  { code: "ar", label: "العربية" },
  { code: "fr", label: "Français" },
  { code: "es", label: "Español" },
  { code: "pt", label: "Português" },
];

function applyGoogleLang(code) {
  const trySet = (n) => {
    const combo = document.querySelector(".goog-te-combo");
    if (combo) {
      combo.value = code;
      combo.dispatchEvent(new Event("change"));
      return true;
    }
    if (n < 20) setTimeout(() => trySet(n + 1), 300);
    return false;
  };
  trySet(0);
  document.documentElement.dir = code === "ar" ? "rtl" : "ltr";
  document.documentElement.lang = code;
}

export default function LanguageSwitcher({ testId = "lang" }) {
  const [open, setOpen] = useState(false);
  const [lang, setLang] = useState(() => localStorage.getItem("ao2_lang") || "en");
  const ref = useRef(null);

  useEffect(() => {
    if (lang !== "en") setTimeout(() => applyGoogleLang(lang), 1200);
    document.documentElement.dir = lang === "ar" ? "rtl" : "ltr";
  }, []); // eslint-disable-line

  useEffect(() => {
    const syncLanguage = (event) => {
      const next = event.detail || localStorage.getItem("ao2_lang") || "en";
      setLang(next);
      document.documentElement.dir = next === "ar" ? "rtl" : "ltr";
      document.documentElement.lang = next;
    };
    window.addEventListener("ao2-language-change", syncLanguage);
    window.addEventListener("storage", syncLanguage);
    return () => {
      window.removeEventListener("ao2-language-change", syncLanguage);
      window.removeEventListener("storage", syncLanguage);
    };
  }, []);

  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const choose = (code) => {
    setLang(code);
    localStorage.setItem("ao2_lang", code);
    window.dispatchEvent(new CustomEvent("ao2-language-change", { detail: code }));
    setOpen(false);
    applyGoogleLang(code);
  };

  const current = LANGS.find((l) => l.code === lang) || LANGS[0];

  return (
    <div className="relative notranslate" ref={ref} translate="no">
      <button
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1.5 text-xs font-body text-silver/70 hover:text-gold transition-colors"
        data-testid={`${testId}-switcher`}
        aria-label="Change language"
      >
        <Globe size={15} /> {current.code.toUpperCase()} <ChevronDown size={13} className={open ? "rotate-180 transition-transform" : "transition-transform"} />
      </button>
      {open && (
        <div className="absolute right-0 mt-3 w-40 rounded-xl glass border border-gold/25 py-2 z-[80]" data-testid={`${testId}-menu`}>
          {LANGS.map((l) => (
            <button
              key={l.code}
              onClick={() => choose(l.code)}
              className="w-full flex items-center justify-between px-4 py-2 text-sm font-body text-silver/80 hover:text-gold hover:bg-white/5 transition-colors"
              data-testid={`${testId}-${l.code}`}
            >
              {l.label} {lang === l.code && <Check size={14} className="text-gold" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
